import { useEffect, useRef, useState, useCallback } from 'react';
import { usePlaylist } from '../contexts/PlaylistContext';

interface AudioAnalysis {
  frequencyData: Uint8Array<ArrayBuffer> | null;
  kickLevel: number;
  bassLevel: number;
  midLevel: number;
  trebleLevel: number;
  overallLevel: number;
}

export interface BandLevels {
  kickLevel: number;
  bassLevel: number;
  midLevel: number;
  trebleLevel: number;
  overallLevel: number;
}

export function bandLevels(data: ArrayLike<number>, binCount: number): BandLevels {
  const kickEnd = Math.floor(binCount * 0.03);
  const bassEnd = Math.floor(binCount * 0.1);
  const midEnd = Math.floor(binCount * 0.5);
  let kickSum = 0;
  let bassSum = 0;
  let midSum = 0;
  let trebleSum = 0;
  for (let i = 0; i < binCount; i++) {
    const value = data[i] ?? 0;
    if (i < kickEnd) kickSum += value;
    else if (i < bassEnd) bassSum += value;
    else if (i < midEnd) midSum += value;
    else trebleSum += value;
  }
  const kickLevel = kickSum / Math.max(1, kickEnd) / 255;
  const bassLevel = bassSum / Math.max(1, bassEnd - kickEnd) / 255;
  const midLevel = midSum / Math.max(1, midEnd - bassEnd) / 255;
  const trebleLevel = trebleSum / Math.max(1, binCount - midEnd) / 255;
  return { kickLevel, bassLevel, midLevel, trebleLevel, overallLevel: (kickLevel + bassLevel + midLevel + trebleLevel) / 4 };
}

export function useAudioAnalyser(enabled: boolean = true): AudioAnalysis {
  const { analyserNode, isPlaying } = usePlaylist();
  const [analysis, setAnalysis] = useState<AudioAnalysis>({
    frequencyData: null,
    kickLevel: 0,
    bassLevel: 0,
    midLevel: 0,
    trebleLevel: 0,
    overallLevel: 0,
  });
  const rafRef = useRef<number | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

  const updateAnalysis = useCallback(() => {
    if (!analyserNode || !isPlaying || !enabled) {
      if (analysis.overallLevel !== 0) {
        setAnalysis({
          frequencyData: null,
          kickLevel: 0,
          bassLevel: 0,
          midLevel: 0,
          trebleLevel: 0,
          overallLevel: 0,
        });
      }
      return;
    }

    if (!dataArrayRef.current) {
      dataArrayRef.current = new Uint8Array(analyserNode.frequencyBinCount);
    }

    analyserNode.getByteFrequencyData(dataArrayRef.current);

    setAnalysis({
      frequencyData: dataArrayRef.current,
      ...bandLevels(dataArrayRef.current, analyserNode.frequencyBinCount),
    });
  }, [analyserNode, isPlaying, enabled, analysis.overallLevel]);

  useEffect(() => {
    if (!enabled || !isPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    let lastTime = 0;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const loop = (currentTime: number) => {
      rafRef.current = requestAnimationFrame(loop);

      const delta = currentTime - lastTime;
      if (delta < frameInterval) return;
      lastTime = currentTime - (delta % frameInterval);

      updateAnalysis();
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, isPlaying, updateAnalysis]);

  return analysis;
}

export default useAudioAnalyser;
